import { LinearProgress } from "@mui/material";
import { Box, Breadcrumbs, Button, Typography } from "@mui/material";
import { useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { deleteTrackFile } from "../../services/trackService";
import { fetchUserTrackFiles } from "../../services/trackService";
import { formatFileSize, getAssetSize } from "../../services/trackService";
import { AuthContext } from "../../contexts";
import { Folder, GetFoldersResponse } from "../../proto/api_pb";
import { GetTrackFilesResponse, TrackFile } from "../../proto/api_pb";
import { FileState, GetAssetSizeResponse } from "../../proto/api_pb";
import { FileObj } from "../MyFiles/types";
import { TrackFiles } from "../../components/TrackFile/TrackFile";
import { TrackDeleteConfirmModal } from "../../components/Modal/TrackDeleteConfirmModal";
import { toast } from "react-toastify";
import { RpcError } from "grpc-web";
import CollabModal from "../../components/Modal/CollabModal";
import PageLayout from "../../components/PageLayout/PageLayout";
import { FoldersSection } from "../../components/FoldersSection/FoldersSection";
import StorageComponent from "../../components/StorageComponent/StorageComponent";
import { TopContainer, styles } from "../MyFiles/styles";
import { TrackMenuButton } from "../../components/TrackComponents/TrackMenuButton/TrackMenuButton";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import { CreateFolderModal } from "../../components/Modal/CreateFolderModal";
import AddTrackFileModal from "../../components/Modal/AddTrackFileModal";
import MultipleFileUploadModal from "../../components/Modal/MultipleFilesUploadModal";
import { BreadCrumbsObj, FolderPageProps } from "./types";
import { getAllFolders } from "../../services/folderService";
import { APP_COLOR, TEXT_COLOR } from "../../theme";

export const FolderPage: React.FC<FolderPageProps> = ({
  getStorageUsed,
  setUploadingFilesDetailsList,
  setIsUploading,
  setUploadingCardsStatusList,
  setBreadCrumbs,
  breadCrumbs,
}) => {
  const [files, setFiles] = useState<any[]>();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [fileToDeleteId, setFileToDeleteId] = useState("");
  const [openCollabsModal, setOpenCollabsModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<TrackFile>();
  const [selectedFiles, setSelectedFiles] = useState<FileObj[]>([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [openAddTrackFileModal, setOpenAddTrackFileModal] = useState(false);
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] =
    useState<boolean>(false);
  const [isMultipleFileUploadModalOpen, setIsMultipleFileUploadModal] =
    useState(false);
  const [foldersList, setFoldersList] = useState<Folder[]>([]);
  const [isFilesLoaded, setIsFilesLoaded] = useState<boolean>(false);
  const { getValidAccessToken } = useGetValidAccessToken();
  const { userId } = useContext(AuthContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation();

  const handleDragStart = (index: any) => {
    setDraggedIndex(index);
  };

  const handleFetchTrackFiles = useCallback(async () => {
    setIsFilesLoaded(true);
    const token = await getValidAccessToken();
    try {
      const response = (await fetchUserTrackFiles(
        token,
        userId
      )) as GetTrackFilesResponse;
      const filesList = response.getTrackFilesList();
      const filesObjList: FileObj[] = [];
      for (const file of filesList) {
        if (
          file.getFileState() === FileState.FILE_STATE_ACTIVE &&
          file.getFolderId() === id
        ) {
          try {
            const fileSize = (await getAssetSize(
              token,
              file.getFileUrl()
            )) as GetAssetSizeResponse;
            filesObjList.push({
              trackFile: file,
              fileSize: fileSize.getSizeGb() ? fileSize.getSizeGb() : 0,
            });
            setSelectedFiles(filesObjList);
          } catch (error) {
            console.log(error);
          }
        }
      }
      setFiles([...filesObjList]);
      setIsFilesLoaded(false);
    } catch (e) {
      console.log(e);
      setIsFilesLoaded(false);
    }
  }, [getValidAccessToken, id, userId]);

  const handleOpenDeleteModal = (id: string) => {
    setIsDeleteModalOpen(true);
    setFileToDeleteId(id);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteFile = async () => {
    if (files) {
      const updatedFiles = files.filter(
        (file) => file.trackFile.getId() !== fileToDeleteId
      );
      setFiles([...updatedFiles]);
      const deleteTrackFileResponse = await deleteTrackFile(
        fileToDeleteId,
        await getValidAccessToken()
      );
      if (deleteTrackFileResponse instanceof RpcError) {
        throw deleteTrackFileResponse;
      }
      handleCloseDeleteModal();
      toast.success("File removed successfully");
    }
  };

  const handleOpenCollabsModal = (id: string) => {
    if (files) {
      setOpenCollabsModal(true);
      const fl = files.filter((f) => f.trackFile.getId() === id)[0];
      setSelectedFile(fl.trackFile);
    }
  };

  const handleCloseCollabsModal = () => {
    setOpenCollabsModal(false);
  };

  const handleAddFile = () => {
    setOpenAddTrackFileModal(true);
  };

  const handleAddFiles = () => {
    setIsMultipleFileUploadModal(true);
  };

  const handleCloseAddFileModal = (isAdded: boolean, message: string) => {
    if (isAdded) {
      toast.info("File Successfully Added");
    }
    setOpenAddTrackFileModal(false);
  };

  const handleFileAdded = () => {
    getStorageUsed();
    setOpenAddTrackFileModal(false);
  };

  const handleCloseMultipleFileUploadModal = () => {
    setIsMultipleFileUploadModal(false);
  };

  const getAllFoldersList = async () => {
    const token = await getValidAccessToken();
    const res = await getAllFolders(token, userId);
    if (res instanceof GetFoldersResponse) {
      setFoldersList(
        res
          .getFoldersList()
          .filter(
            (folder) =>
              folder.getParentFolderId() === id &&
              folder.getFolderState() ===
                (FileState.FILE_STATE_ACTIVE || FileState.FILE_STATE_UNDEFINED)
          )
      );
    }
  };

  useEffect(() => {
    if (userId !== "") {
      handleFetchTrackFiles();
    }
  }, [userId, getValidAccessToken, handleFetchTrackFiles]);

  useEffect(() => {
    if (id) {
      const obj: BreadCrumbsObj = {
        folderName: state.name,
        id: id,
      };
      const idExists = breadCrumbs.some((item) => item.id === obj.id);
      if (!idExists) {
        setBreadCrumbs((prevArray) => [...prevArray, obj]);
      }
    }
  }, [setBreadCrumbs, id, state.name, breadCrumbs]);

  return (
    <>
      {isMultipleFileUploadModalOpen && (
        <MultipleFileUploadModal
          open={isMultipleFileUploadModalOpen}
          handleClose={handleCloseMultipleFileUploadModal}
          handleFileAdded={handleFileAdded}
          setUploadingFilesDetailsList={setUploadingFilesDetailsList}
          setIsUploading={setIsUploading}
          setUploadingCardsStatusList={setUploadingCardsStatusList}
          parentFolderId={id}
        />
      )}
      {openAddTrackFileModal && (
        <AddTrackFileModal
          open={openAddTrackFileModal}
          handleClose={handleCloseAddFileModal}
          handleFileAdded={handleFileAdded}
          parentFolderId={id}
        />
      )}
      {isCreateFolderModalOpen && (
        <CreateFolderModal
          setIsCreateFolderModalOpen={setIsCreateFolderModalOpen}
          isCreateFolderModalOpen={isCreateFolderModalOpen}
          parentFolderId={id}
          getAllFoldersList={getAllFoldersList}
        />
      )}
      <PageLayout>
        {openCollabsModal && (
          <CollabModal
            openCollabsModal={openCollabsModal}
            handleCloseCollabsModal={handleCloseCollabsModal}
            trackToEdit={selectedFile}
            handleFetchTrackFiles={handleFetchTrackFiles}
          />
        )}
        {isDeleteModalOpen && (
          <TrackDeleteConfirmModal
            deleteConfirmModalOpen={isDeleteModalOpen}
            closeDeleteConfirmModal={handleCloseDeleteModal}
            handleDeleteTrack={handleDeleteFile}
            type="File"
          />
        )}
        <Box sx={{ margin: "15px", marginBottom: "20px", minHeight: "50vh" }}>
          <Box>
            <Breadcrumbs
              sx={{ marginLeft: "10px" }}
              separator=">"
              aria-label="breadcrumb"
            >
              <Typography
                sx={{ cursor: "pointer" }}
                onClick={() => navigate(`/my-files`)}
              >
                Files
              </Typography>
              {breadCrumbs.map((folder, i) => {
                return (
                  <Typography
                    key={i}
                    sx={{
                      cursor: "pointer",
                      color: folder.id === id ? APP_COLOR : TEXT_COLOR,
                    }}
                    onClick={() =>
                      navigate(`/folder/${folder.id}`, {
                        state: { name: folder.folderName },
                      })
                    }
                  >
                    {folder.folderName}
                  </Typography>
                );
              })}
            </Breadcrumbs>

            <Box sx={{ margin: "10px" }}>
              <TopContainer>
                <TrackMenuButton
                  handleAddTrack={handleAddFile}
                  handleAddTracks={handleAddFiles}
                  isMultipleFileUploadButton={true}
                />
                <Button
                  onClick={() =>
                    setIsCreateFolderModalOpen(!isCreateFolderModalOpen)
                  }
                  sx={styles.button}
                >
                  <CreateNewFolderIcon
                    sx={{ width: "20px", margin: "5px", marginTop: "2px" }}
                  />
                  Create Folder
                </Button>
                <StorageComponent />
              </TopContainer>
            </Box>
            <FoldersSection
              draggedIndex={draggedIndex}
              selectedFiles={selectedFiles}
              files={files}
              getAllFoldersList={getAllFoldersList}
              foldersList={foldersList}
              handleFetchTrackFiles={handleFetchTrackFiles}
            />
            {isFilesLoaded && (
              <Box>
                <Typography sx={{ textAlign: "center" }} variant="h5">
                  Getting files ...
                </Typography>
                <LinearProgress sx={{ margin: "30px 0px 30px 0px" }} />
              </Box>
            )}
            {files && files.length > 0 && (
              <Box>
                <Typography sx={{ marginLeft: "10px" }} variant="h5">
                  {files.length === 1 ? "File" : "Files"}
                </Typography>
                {files &&
                  files.map((el: FileObj, i) => {
                    return (
                      <div
                        key={i}
                        draggable
                        onDragStart={() => handleDragStart(i)}
                      >
                        <TrackFiles
                          key={i}
                          url={el.trackFile.getFileUrl()}
                          label={el.trackFile.getLabel()}
                          timeStamp={el.trackFile.getTimestamp()}
                          ownerId={el.trackFile.getOwnerId()}
                          fileName={el.trackFile.getFilename()}
                          fileType={el.trackFile.getType()}
                          fileSize={
                            el.fileSize ? formatFileSize(el.fileSize) : "-"
                          }
                          handleOpenDeleteModal={handleOpenDeleteModal}
                          fileId={el.trackFile.getId()}
                          handleOpenCollabsModal={handleOpenCollabsModal}
                          collaborators={el.trackFile.getCollaboratorsList()}
                          file={el.trackFile}
                        />
                      </div>
                    );
                  })}
              </Box>
            )}
          </Box>
        </Box>
      </PageLayout>
    </>
  );
};
